import React from 'react';
import { Music, Check, Link2, Clock } from 'lucide-react';

/**
 * "What's new": song choices go out once per project. CLIENTS who have us choose their music
 * (operator ruling, 2026-09-15; the audience check is routes/announcements.has_us_choose_music).
 *
 * THE WORDING IS THE OPERATOR'S, approved sentence by sentence, and every sentence is here word for
 * word. Only the layout is ours: the operator found one paragraph too much to read, so the same
 * sentences are broken into an opening line, the bold rule, three icon rows (the icons are the
 * graphic, drawn in markup so they follow the theme), the reason, and the thanks. Do not tidy the
 * wording; re-group it only if the operator asks.
 *
 * THE RED "WHO THIS IS FOR" LINE comes first on purpose (operator, 2026-09-15). The audience is
 * clients who have ever asked us to choose their music, and many of them also send their own
 * songs. Without saying up front that sending your own songs needs no action, the notice read as
 * an invitation to ask us to pick songs.
 */

const Rule = ({ icon: Icon, tone, warning, children }) => (
  <div className="flex items-start gap-3 rounded-lg bg-ug-surface border border-ug-border-strong px-3 py-2.5">
    {/* `warning` says what the line IS, so the site look can give it the warning colour (ember) rather than guessing */}
    <span data-tone={warning ? 'warning' : undefined} className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${tone}`}>
      <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
    </span>
    <p className="text-[13px] text-zinc-100/85 leading-relaxed pt-1">{children}</p>
  </div>
);

export function SongOnceClientAnnouncement({ onAcknowledge }) {
  return (
    <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl"
         data-testid="whats-new-song-once-client">
      <div className="p-6 pb-5 border-b border-ug-border-subtle">
        <div className="flex items-center gap-2 mb-3">
          <Music className="w-3.5 h-3.5 text-ug-gold" strokeWidth={2} />
        </div>
        <h2 className="font-heading text-2xl text-zinc-100">A quick note about song choices.</h2>
      </div>
      <div className="p-6 py-5 flex flex-col gap-4">
        <div className="rounded-lg border border-ug-status-red/40 bg-ug-status-red/[0.06] px-3 py-2.5" data-testid="song-once-client-announcement-audience">
          {/* text-red-300, not text-ug-status-red: the token measured 4.50:1 on this tinted box in dark.
              index.css maps red-300 to red-700 in light. */}
          <p className="text-[13px] font-semibold text-red-300 leading-relaxed">
            This applies only if you ask us to choose the songs for your films. If you send us your own songs, no action is needed.
          </p>
        </div>

        <div className="flex flex-col gap-4" data-testid="song-once-client-announcement-body">
          <p className="text-[14px] text-zinc-100/80 leading-relaxed">
            Our editors love finding the right music for your films.
          </p>
          <p className="text-[16px] font-bold text-zinc-100 leading-snug">
            We now send song choices only once per project.
          </p>

          <div className="flex flex-col gap-2">
            <Rule icon={Check} tone="bg-oxidized-teal/15 text-oxidized-teal">
              Choose the song(s) you'd like us to use.
            </Rule>
            <Rule icon={Link2} tone="bg-ug-gold/15 text-ug-gold">
              If you don't like one or all of our options, you are required to send links to your own song(s) instead, within 24 hours of our email.
            </Rule>
            <Rule icon={Clock} tone="bg-amber-400/15 text-amber-300" warning>
              If we don't hear back within those 24 hours, your film loses its spot in the editing queue and other films move ahead.
            </Rule>
          </div>

          <div className="rounded-lg bg-ug-surface/50 border border-ug-border-subtle border-l-2 border-l-ug-gold/60 px-3 py-2.5">
            <p className="text-[13px] text-zinc-100/75 leading-relaxed">
              <span className="font-semibold text-zinc-100">Here's why:</span> every round of back and forth costs days, and that delay
              ripples through the queue and pushes back films for many other clients, especially during busy times of year.
              One reply with everything you want keeps your film, and everyone else's, on schedule.
            </p>
          </div>

          <p className="text-[14px] text-zinc-100/80 leading-relaxed">Thank you!</p>
        </div>
      </div>
      <div className="p-6 pt-5 border-t border-ug-border-subtle bg-ug-surface/20">
        <button data-testid="whats-new-next" onClick={() => onAcknowledge && onAcknowledge()}
                className="tap-auto w-full py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider">
          Got it
        </button>
      </div>
    </div>
  );
}

export default SongOnceClientAnnouncement;
